import React, {
    useEffect, useState, useContext, useCallback
} from 'react';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Loader, useModal } from '@jutro/components';
import { withRouter } from 'react-router-dom';
import { Wizard } from '@xengage/gw-portals-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { QuoteInfoComponentContext } from 'gw-capability-quoteandbind-common-react';
import { MockUpUtil } from '@xengage/gw-portals-util-js';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { MLTagManagerService } from 'ml-portals-services-react';
import { Submission } from 'gw-capability-quoteandbind';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { ClauseService } from 'gw-capability-policycommon';
import { CustomQuoteInfoComponent } from 'ml-capability-quoteandbind-common-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';

import wizardStepToFieldMapping from './config/pa-wizard-step-to-field-mapping.json5';
import wizardConfig from './config/pa-wizard-config.json5';
import './PAWizard.messages';

const PATH_TO_MOCK_DATA = 'quote.pa';
const MOCK_DATA_TO_SET = [
    'baseData.accountHolder.emailAddress1',
    'baseData.accountHolder.accountHolder',
    'baseData.policyAddress.addressLine1',
    'baseData.policyAddress.city',
    'baseData.policyAddress.state',
    'baseData.productCode',
    'baseData.accountHolder.firstName',
    'baseData.accountHolder.lastName',
    'baseData.accountHolder.subtype',
    'baseData.accountHolder.dateOfBirth.day',
    'baseData.accountHolder.dateOfBirth.month',
    'baseData.accountHolder.dateOfBirth.year'
];
const MOCK_DATA_TO_REMOVE = [
    'baseData.accountHolder.emailAddress1',
    'baseData.accountHolder.firstName',
    'baseData.accountHolder.lastName',
    'baseData.accountHolder.dateOfBirth',
    'baseData.policyAddress.addressLine1',
    'baseData.policyAddress.city'
];

const ACTION_MSG = {
    cancelAction: {
        title: commonMessages.wantToCancel,
        message: commonMessages.infoWillBeSavedAsDraft,
        status: 'warning',
        icon: 'mi-error-outline',
        confirmButtonText: commonMessages.yes,
        cancelButtonText: commonMessages.close
    }
};

function setCoverageStartDate() {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + 1);
    const coverageStartDate = MLLocalDateUtil.date2LocalDate(currentDate);
    return coverageStartDate;
}

function setPrepData(data) {
    const realData = {
        baseData: {
            policyAddress: data.address,
            periodStartDate: setCoverageStartDate(),
            sameMailingAddress_ML: true,
            priorAddressQuestion_Ext: false,
            productCode: data.productCode
        }
    };

    return MockUpUtil.setMockData(realData, PATH_TO_MOCK_DATA, ...MOCK_DATA_TO_SET);
}

function setPrepDataFFQ(data) {
    const realData = {
        baseData: {
            policyAddress: data?.account?.accountHolder?.primaryAddress,
            periodStartDate: setCoverageStartDate(),
            sameMailingAddress_ML: true,
            priorAddressQuestion_Ext: false,
            productCode: data.productCode,
            primaryNamedInsured_ML: {
                firstName: data?.account?.accountHolder?.firstName,
                lastName: data?.account?.accountHolder?.lastName,
                middleName: data?.account?.accountHolder?.middleName,
                suffix: data?.account?.accountHolder?.suffix,
                dateOfBirth: data?.account?.accountHolder?.dateOfBirth,
                emailAddress1: data?.account?.accountHolder?.emailAddress1,
            },
            accountNumber: data?.account?.accountNumber
        }
    };

    return MockUpUtil.setMockData(realData, PATH_TO_MOCK_DATA, ...MOCK_DATA_TO_SET);
}

function removeMockData(submission) {
    return MockUpUtil.cleanUpMockedProperties(
        submission,
        PATH_TO_MOCK_DATA,
        ...MOCK_DATA_TO_REMOVE
    );
}

function createSubmissionOnPartialAddress(address) {
    const data = setPrepData(address);
    return LoadSaveService.createSubmission(data);
}

function PAWizard(props) {
    const modalApi = useModal();
    const { steps, title } = wizardConfig;
    const { authHeader } = useAuthentication();    
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [shouldSkipValidSteps, setShouldSkipValidSteps] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const { location, history } = props;

    useEffect(() => {
        const viewModelContext = {
            AccountEmailRequired: false,
            DriverEmailRequired: true,
            AccountDOBRequired: true
        };

        if (!viewModelService) {
            if (_.isEmpty(location.search)) {
                history.push('/');
            }

            return;
        }

        let submissionPromise;
        if (_.has(location, 'state.account')) {
            const data = setPrepDataFFQ(location.state);
            submissionPromise = Promise.resolve(data);
        } else if (_.has(location, 'state.address') || _.has(location, 'state.accountNo')) {
            const data = setPrepData(location.state);
            submissionPromise = Promise.resolve(data);
        } else if (_.has(location, 'state.submission')) {
            const { submission } = location.state;

            if(submission?.hvqQuoteID_ML) {
                setShouldSkipValidSteps(false);
            } else {
                setShouldSkipValidSteps(true);
            }
            submissionPromise = Promise.resolve(submission);
        } else if (_.has(location, 'search')) {
            if (location.search === "") {
                history.push('/');
                return;
            } else {
                const parsedParms = queryString.parse(location.search);
                submissionPromise = LoadSaveService.retrieveSubmission(parsedParms);
            }
        } else {
            return;
        }

        submissionPromise.then((response) => {
            ClausesUtil.getDependentClausesFromServer(
                ClauseService,
                _.get(response, 'quoteID'),
                authHeader
            );
            const submissionVM = viewModelService.create(
                new Submission(removeMockData(response)),
                'pc',
                'edge.capabilities.quote.submission.dto.QuoteDataDTO',
                viewModelContext
            );

            setInitialSubmission(submissionVM);
            setIsLoading(false);
        });
        // only execute this once per component lifecycle
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            MLTagManagerService.setModel();
        }
    }, []);

    useEffect(() => {    
        if (typeof Invoca !== 'undefined') {      
            Invoca.PNAPI.run();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const handleCancel = useCallback(
        (wizardProps) => {
            const { wizardSnapshot, wizardData } = wizardProps;
            const latestSnapshot = wizardData.aspects.subtreeValid && wizardData.aspects.valid
                ? wizardData
                : wizardSnapshot;
            
            // Allow transition to Bundle Cart
            if(wizardProps.param === '/bundle-cart'){
                return true;
            }

            if(sessionStorage.getItem("pdVerificationError") === "true") {
                sessionStorage.removeItem("pdVerificationError");
                return true;
            }

            const periodStatus = _.get(wizardSnapshot, 'baseData.periodStatus.value.code');
            if (periodStatus === 'Bound') {
                // allow transition once the submission is bound
                return true;
            }
            return modalApi.showConfirm(ACTION_MSG.cancelAction).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                let serverCall = LoadSaveService.updateDraftSubmission;

                if (periodStatus === 'Quoted') {
                    serverCall = LoadSaveService.updateQuotedSubmission;
                }

                const wizardDataFromSnapshot = _.get(latestSnapshot, 'value');
                const dataToSave = periodStatus === 'Quoted'
                    ? MockUpUtil.setMockData(wizardDataFromSnapshot, PATH_TO_MOCK_DATA)
                    : MockUpUtil.setMockData(
                        wizardDataFromSnapshot,
                        PATH_TO_MOCK_DATA,
                        ...MOCK_DATA_TO_SET
                    );
                setIsLoading(true);

                serverCall(dataToSave)
                    .then(() => {
                        history.push('/');
                    })
                    .catch(() => {
                        modalApi.showAlert({
                            title: commonMessages.unableToSaveQuote,
                            message: commonMessages.unableToSaveQuoteMessage,
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: commonMessages.ok
                        }).catch(_.noop);
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
                return true;
            }, _.noop);
        },
        [history]
    );

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (!initialSubmission) {
        return null;
    }

    const submissionStatus = _.get(initialSubmission, 'baseData.status_ML.value');
    const isBindSubmission = !!_.get(initialSubmission, 'bindData.policyNumber.value') && (submissionStatus === 'Bound' || submissionStatus === 'Binding');

    return (
        <QuoteInfoComponentContext.Provider value={CustomQuoteInfoComponent}>
            <Wizard
                initialSteps={isBindSubmission ? [steps[steps.length -1]] : steps}
                wizardTitle={title}
                initialData={initialSubmission}
                onCancel={handleCancel}
                skipCompletedSteps={shouldSkipValidSteps}
                wizardStepToFieldMapping={wizardStepToFieldMapping}
            />
        </QuoteInfoComponentContext.Provider>
    );
}

PAWizard.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string,
        state: PropTypes.shape({
            address: PropTypes.shape({}),
            submission: PropTypes.shape({})
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired
};

export default withRouter(PAWizard);
